/**
 * Created by Alex Xia on 08/03/2019.
 * Description:
 *  Float button
 * ------ maintenance history ------
 * 06/28/2019 Bowen Li  make the float menu drage
 */
import { Component, OnInit, ElementRef, Renderer2, OnDestroy, ViewChild, AfterViewInit, AfterViewChecked } from '@angular/core';
import { FloatMenuService, DraftActionType } from '../../services/float-menu.service';
import { Subscription, Subject } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../redux';
import { getDraftListState } from '../../redux/reducers/draft-view.reducer';
import { DataList } from '../../tamalelibs/models/data-list.model';
import { Thread } from '../../tamalelibs/models/thread.model';
import { QueryInfo, ThreadListConfig, SortInfo } from '../../pages/research/research.view-model';
import { KeywordSearchConfig } from '../../widgets/keyword-search/keyword-search.component';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';
import { CREATE_DRAFT_SUCCESS, DELETE_DRAFT_SUCCESS, UPDATE_DRAFT_SUCCESS } from '../../tamalelibs/redux/note.actions';
import { filter } from 'rxjs/operators';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { ToastService } from '../../widgets/toast/toast.service';
import { DraftViewActionTypes } from '../../redux/actions/draft-view.actions';

@Component({
    selector: 'float-menu-draft-panel',
    templateUrl: './float-menu-draft-panel.component.html',
    styleUrls: ['./float-menu-draft-panel.component.scss'],
})
export class FloatMenuDraftPanelComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
    @ViewChild('draftPanel', { static: false }) draftPanel: ElementRef;

    threadListConfig: ThreadListConfig = new ThreadListConfig();
    keywordSearchConfig: KeywordSearchConfig = new KeywordSearchConfig();
    sortChangeSubject: Subject<any> = new Subject();
    sortInfo: SortInfo = new SortInfo();
    draftCount: number;
    position: string; // float menu position : left or right

    private _queryInfo: QueryInfo = new QueryInfo();
    private _element: HTMLElement;
    private _destroySubscriptions: Array<Subscription> = [];
    private _refreshManually = false;

    constructor(
        public _floatMenuService: FloatMenuService,
        private _store: Store<AppState>,
        private _elementRef: ElementRef,
        private _renderer: Renderer2,
        private _toast: ToastService,
    ) {
        this._element = this._elementRef.nativeElement;
    }

    ngOnInit() {
        this.sortInfo.sortBy = 'lastModifiedDate';
        this.threadListConfig.threadListSelector = getDraftListState;
        this.threadListConfig.reloadAction = DraftViewActionTypes.RELOAD;
        this.threadListConfig.loadMoreAction = DraftViewActionTypes.LOAD_MORE;

        this._destroySubscriptions.push(
            this.keywordSearchConfig.events$.subscribe(event => this._keywordSearchEventHandler(event)),

            this._store.pipe<any>(
                select(getDraftListState)
            ).subscribe(res => this._onSubscribeDraftListItem(res)),

            this._store.pipe<any>(
                select(lastCustomActionSelector)
            ).pipe(
                filter(action => action.type.indexOf('DRAFT') > -1)
            ).subscribe(action => this._actionMessageHandler(action)),
            this._floatMenuService.actionSubject$.subscribe(action => {
                if (action.type === DraftActionType.RELOAD) {
                    this._reloadDraftList();
                }
            }),
            // get the float menu location
            this._floatMenuService.floatMenuSubject$.subscribe(result => {
                this.position = result['position'];
            }),
        );
        this._initSubComponentSortState();
    }

    ngAfterViewInit() {
        this._reloadDraftList();
    }

    ngAfterViewChecked() {
        const draftPanelClass = '.float-menu-draft-panel-container';
        if (this._element.querySelector(draftPanelClass) != null) {
            const panelHeight = this._floatMenuService.getHeightOfFloatMenuDraftPanel(this.draftCount);
            this._renderer.setStyle(this._element.querySelector(draftPanelClass), 'height', panelHeight.toString() + 'px');
            this._renderer.setStyle(this._element.querySelector(draftPanelClass), 'min-height', '360px');
            this._renderer.setStyle(this._element.querySelector(draftPanelClass), 'max-height', '830px');
            // control the panel show position
            if (this.position === 'bottomRight') {
                this._renderer.setStyle(this._element.querySelector(draftPanelClass), 'left', 'auto');
                this._renderer.setStyle(this._element.querySelector(draftPanelClass), 'right', 0);
            } else {
                this._renderer.setStyle(this._element.querySelector(draftPanelClass), 'left', 0);
                this._renderer.setStyle(this._element.querySelector(draftPanelClass), 'right', 'auto');
            }
        }
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    showFloatMenuWithoutDraftList() {
        this._floatMenuService.floatMenuSubject$.next({
            onlyShowFloatMenu: true,
            draftCount: this.draftCount,
            keywordSearch: this._queryInfo.searchText
        });
    }

    onSortSelect(e) {
        if (e.payload.data.sortBy) {
            this._queryInfo.sortBy = e.payload.data.sortBy;
            this.sortInfo.sortBy = e.payload.data.sortBy;
        }
        if (e.payload.data.sortOrder) {
            this._queryInfo.sortOrder = e.payload.data.sortOrder;
            this.sortInfo.sortOrder = e.payload.data.sortOrder;
        }
        this._reloadDraftList();
    }

    onRefreshClicked() {
        this._refreshManually = true;
        this._reloadDraftList();
    }

    private _actionMessageHandler(action) {
        if (action.type === CREATE_DRAFT_SUCCESS || action.type === DELETE_DRAFT_SUCCESS || action.type === UPDATE_DRAFT_SUCCESS) {
            this._reloadDraftList();
        }
        if (action.type === DELETE_DRAFT_SUCCESS) {
            this._toast.notify({
                message: 'Draft is deleted.',
                style: NotificationStyles.Success
            });
        }
        if (action.type === DraftViewActionTypes.RELOAD_SUCCESS && this._refreshManually) {
            this._refreshManually = false;
            this._toast.notify({
                message: 'Draft list is refreshed.',
                style: NotificationStyles.Info
            });
        }
    }

    private _initSubComponentSortState() {
        this.sortChangeSubject.next(this.sortInfo);
    }

    private _onSubscribeDraftListItem(items: DataList<Thread>) {
        if (items) {
            this.draftCount = items.totalCount;
        }
    }

    private _keywordSearchEventHandler(event) {
        if (event.type === 'CHANGE') {
            this._queryInfo.searchText = event.payload.data;
            this._reloadDraftList();
        }
    }

    private _reloadDraftList() {
        this.threadListConfig.actions$.next({
            type: this.threadListConfig.reloadAction
        });

        if (!this._refreshManually) {
            this._queryInfo.sortBy = this.sortInfo.sortBy;
            this._queryInfo.sortOrder = this.sortInfo.sortOrder;

            this._store.dispatch({
                type: DraftViewActionTypes.SET_QUERY_INFO,
                payload: this._queryInfo
            });

            this._store.dispatch({
                type: DraftViewActionTypes.SET_SORT_INFO,
                payload: this.sortInfo
            });
        }

        this._store.dispatch({
            type: this.threadListConfig.reloadAction,
            payload: {
                pageIndex: 1,
                pageSize: 20,
                ...this._queryInfo
            }
        });
    }
}
